import { useState, useRef, useEffect } from "react";
import ariaAvatar from "./assets/aria-avatar.jpeg";
import { authHeader } from './supabase';

const SYSTEM_PROMPT = `Sos ARIA, la asistente de inteligencia artificial integrada en ALQOS™, un sistema de gestión integral para la industria gráfica desarrollado por AS · QORVEX.

ALQOS™ gestiona: ISO 9001:2015, ISO 14001:2015, FSC Cadena de Custodia, FSSC 22000 y HACCP.

Módulos disponibles en el sistema:
- Calidad: No Conformidades, FMEA, Plan de Control, Acciones Correctivas, Análisis Causa Raíz, Capabilidad/MSA
- Auditorías: Auditorías internas, Programa de auditorías
- Inocuidad: HACCP (7 etapas Codex), POEs con versionado y firma digital
- Operaciones: Órdenes de Trabajo, Producción, Pre Prensa, TPM Mantenimiento, Ingreso de Materiales
- Sustentabilidad: Huella de Carbono (ISO 14064), FSC Arqueo, Módulo Sustentabilidad
- Comercial: Clientes, Proveedores, Compras, Cotización, Ventas
- Soporte: RRHH, Capacitaciones, H&S, Ciberseguridad, Oficina Técnica
- Dashboard Calidad Asegurada con insignias Bronce/Plata/Oro

Cómo respondés:
- Directo, claro y práctico — sin rodeos
- En español, tono profesional pero accesible
- Si te preguntan sobre un módulo específico, contextualizás la respuesta en ALQOS™
- Si detectás un problema técnico, sugerís pasos concretos de resolución
- Si no sabés algo específico del sistema, lo decís y orientás al equipo de soporte
- Respuestas cortas por defecto; si necesitan más detalle, lo ofrecés`;

export default function FeedbackWidget({ modulo, usuario, oscuro }) {
  const [abierto, setAbierto] = useState(false);
  const [mensajes, setMensajes] = useState([
    {
      rol: "aria",
      texto: `Hola${usuario?.nombre ? ` ${usuario.nombre}` : ""}! 👋 Soy ARIA, tu asistente de calidad en ALQOS™. ¿En qué te ayudo${modulo && modulo !== "General" ? ` con el módulo ${modulo}` : ""}?`,
    },
  ]);
  const [input, setInput] = useState("");
  const [cargando, setCargando] = useState(false);
  const [cerrando, setCerrando] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const inactividadRef = useRef(null);

  const bg = oscuro ? "#0d2160" : "white";
  const bgHeader = oscuro ? "#091840" : "#1C3FAA";
  const bgInput = oscuro ? "#091840" : "#f7f9fd";
  const borde = oscuro ? "#1e3a8a" : "#dde3f0";
  const textoColor = oscuro ? "#EDF2FA" : "#2F3E2E";
  const bgBurbuja = oscuro ? "#091840" : "#f0f4ff";

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [mensajes, cargando]);
  useEffect(() => { if (abierto) setTimeout(() => inputRef.current?.focus(), 100); }, [abierto]);
  useEffect(() => { return () => clearTimeout(inactividadRef.current); }, []);

  const resetInactividad = () => {
    clearTimeout(inactividadRef.current);
    inactividadRef.current = setTimeout(() => {
      setMensajes((prev) => {
        const ultimo = prev[prev.length - 1];
        if (ultimo?.rol === "aria" && !ultimo?.esInactividad) {
          return [...prev, { rol: "aria", texto: "¿Quedaste con alguna duda? Estoy acá cuando me necesites 😊", esInactividad: true }];
        }
        return prev;
      });
    }, 30000);
  };

  const cerrarConMensaje = () => {
    clearTimeout(inactividadRef.current);
    setCerrando(true);
    setTimeout(() => { setCerrando(false); setAbierto(false); }, 2000);
  };

  const enviar = async () => {
    const texto = input.trim();
    if (!texto || cargando) return;
    const nuevosMensajes = [...mensajes, { rol: "usuario", texto }];
    setMensajes(nuevosMensajes);
    setInput("");
    setCargando(true);
    const historialApi = nuevosMensajes.filter((_, i) => i > 0).map((m) => ({ role: m.rol === "usuario" ? "user" : "assistant", content: m.texto }));
    const primero = historialApi[0];
    if (primero && modulo && modulo !== "General") primero.content = `[Módulo activo: ${modulo}] ${primero.content}`;
    try {
      const res = await fetch("/api/aria", {
        method: "POST",
        headers: { ...(await authHeader()), "Content-Type": "application/json", "anthropic-version": "2023-06-01", },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 1024, system: SYSTEM_PROMPT, messages: historialApi }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setMensajes((prev) => [...prev, { rol: "aria", texto: data.content?.[0]?.text || "No pude generar una respuesta." }]);
      resetInactividad();
    } catch {
      setMensajes((prev) => [...prev, { rol: "aria", texto: "⚠️ No pude conectarme. Verificá la API key en Vercel." }]);
    } finally { setCargando(false); }
  };

  const handleKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviar(); } };

  const limpiarChat = () => setMensajes([{ rol: "aria", texto: `Hola${usuario?.nombre ? ` ${usuario.nombre}` : ""}! 👋 Soy ARIA, tu asistente de calidad en ALQOS™. ¿En qué te ayudo${modulo && modulo !== "General" ? ` con el módulo ${modulo}` : ""}?` }]);

  

  return (
    <>
      {abierto && (
        <div style={{ position:"fixed", bottom:"90px", right:"20px", width:"340px", height:"480px", background:bg, border:`1.5px solid ${borde}`, borderRadius:"18px", boxShadow:"0 8px 40px rgba(9,24,77,0.22)", zIndex:999, fontFamily:"Inter,sans-serif", display:"flex", flexDirection:"column", overflow:"hidden" }}>
          {cerrando && (
            <div style={{ position:"absolute", inset:0, zIndex:10, background:"rgba(28,63,170,0.97)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:"10px", borderRadius:"18px" }}>
              <div style={{ width:"60px", height:"60px", borderRadius:"50%", overflow:"hidden", margin:"0 auto" }}><img src={ariaAvatar} alt="ARIA" style={{ width:"100%", height:"100%", objectFit:"cover" }}/></div>
              <p style={{ fontSize:"15px", fontWeight:"700", color:"white", margin:0 }}>¡Hasta luego!</p>
              <p style={{ fontSize:"12px", color:"rgba(255,255,255,0.7)", margin:0, textAlign:"center", padding:"0 24px" }}>Cualquier consulta volvé cuando quieras 😊</p>
            </div>
          )}
          <div style={{ background:bgHeader, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
            <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
              <div style={{ width:"32px", height:"32px", borderRadius:"50%", overflow:"hidden" }}><img src={ariaAvatar} alt="ARIA" style={{ width:"100%", height:"100%", objectFit:"cover" }}/></div>
              <div>
                <div style={{ fontSize:"13px", fontWeight:"700", color:"white", lineHeight:1.2 }}>ARIA</div>
                <div style={{ fontSize:"10px", color:"rgba(255,255,255,0.65)" }}>Asistente de SGI · ALQOS™</div>
              </div>
            </div>
            <div style={{ display:"flex", gap:"4px" }}>
              <button onClick={limpiarChat} title="Limpiar chat" style={{ background:"rgba(255,255,255,0.12)", border:"none", color:"rgba(255,255,255,0.8)", cursor:"pointer", borderRadius:"8px", padding:"4px 8px", fontSize:"11px" }}>🗑️</button>
              <button onClick={cerrarConMensaje} style={{ background:"rgba(255,255,255,0.12)", border:"none", color:"rgba(255,255,255,0.8)", cursor:"pointer", borderRadius:"8px", padding:"4px 8px", fontSize:"14px" }}>×</button>
            </div>
          </div>
          {modulo && modulo !== "General" && (
            <div style={{ background:oscuro?"#0a1f5c":"#eef2ff", borderBottom:`1px solid ${borde}`, padding:"5px 14px", fontSize:"10px", color:oscuro?"#9aabd4":"#4338ca", flexShrink:0 }}>
              📍 Módulo activo: <strong>{modulo}</strong>
            </div>
          )}
          <div style={{ flex:1, overflowY:"auto", padding:"12px 14px", display:"flex", flexDirection:"column", gap:"10px" }}>
            {mensajes.map((m, i) => (
              <div key={i} style={{ display:"flex", justifyContent:m.rol==="usuario"?"flex-end":"flex-start" }}>
                <div style={{ maxWidth:"82%", background:m.rol==="usuario"?"#2A7FFF":bgBurbuja, color:m.rol==="usuario"?"white":textoColor, borderRadius:m.rol==="usuario"?"14px 14px 4px 14px":"14px 14px 14px 4px", padding:"8px 12px", fontSize:"12px", lineHeight:"1.5", border:m.rol==="aria"?`1px solid ${borde}`:"none" }}>
                  {m.texto}
                </div>
              </div>
            ))}
            {cargando && (
              <div style={{ display:"flex", justifyContent:"flex-start" }}>
                <div style={{ background:bgBurbuja, border:`1px solid ${borde}`, borderRadius:"14px 14px 14px 4px", padding:"8px 14px", fontSize:"18px", display:"flex", gap:"3px", alignItems:"center" }}>
                  <span style={{ animation:"bounce 1s infinite 0s" }}>·</span>
                  <span style={{ animation:"bounce 1s infinite 0.2s" }}>·</span>
                  <span style={{ animation:"bounce 1s infinite 0.4s" }}>·</span>
                </div>
              </div>
            )}
            <div ref={bottomRef}/>
          </div>
          <div style={{ padding:"10px 12px", borderTop:`1px solid ${borde}`, display:"flex", gap:"8px", alignItems:"flex-end", flexShrink:0 }}>
            <textarea ref={inputRef} value={input} onChange={(e)=>setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Preguntá sobre calidad, ISO, módulos..." rows={1} style={{ flex:1, padding:"8px 10px", borderRadius:"10px", border:`1.5px solid ${input.trim()?"#2A7FFF":borde}`, fontSize:"12px", resize:"none", background:bgInput, color:textoColor, outline:"none", fontFamily:"Inter,sans-serif", lineHeight:"1.4", maxHeight:"80px", overflowY:"auto" }}/>
            <button onClick={enviar} disabled={!input.trim()||cargando} style={{ width:"36px", height:"36px", borderRadius:"10px", flexShrink:0, background:input.trim()&&!cargando?"#2A7FFF":"rgba(42,127,255,0.3)", border:"none", color:"white", cursor:input.trim()&&!cargando?"pointer":"default", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"16px" }}>↑</button>
          </div>
        </div>
      )}
      <button onClick={()=>setAbierto((o)=>!o)} style={{ position:"fixed", bottom:"28px", right:"20px", width:"52px", height:"52px", borderRadius:"50%", background:abierto?"#2A7FFF":oscuro?"#0d2160":"white", border:"2px solid #2A7FFF", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"22px", boxShadow:"0 4px 20px rgba(42,127,255,0.35)", zIndex:1000, transition:"all 0.2s" }}>
        {abierto ? "×" : <img src={ariaAvatar} alt="ARIA" style={{ width:"100%", height:"100%", objectFit:"cover", borderRadius:"50%" }}/>}
      </button>
      <style>{`@keyframes bounce{0%,80%,100%{transform:translateY(0);opacity:.4}40%{transform:translateY(-4px);opacity:1}}`}</style>
    </>
  );
}
